import BasicForm, { basicFormButton, type FormButton } from "@/components/form/BasicForm"
import BootstrapButton from "@/components/form/BootstrapButton"
import Icon from "@/components/Icon"
import Modal from "@/components/Modal"
import usePopups from "@/stores/popupsStore"
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons"
import { ref, shallowRef } from "vue"
import { Alert, defineComponent, inferProps, ModalTitle, refSetter, type ReactiveComponent } from "vue-utils"
import DocumentsFileUpload from "./DocumentsFileUpload"
import SelectRecipients from "./SelectRecipients"

interface Props {
	existingRecipients: string[]

	cancel(): void
	upload(files: File[], recipients: string[]): Promise<void>
}

const NoRecipientsConfirm = (
	<Alert title="Confirm Document Upload">
		<p>
			Please confirm that the documents you have selected to upload are correct, and no additional recipients were
			required.
		</p>
		<p>You will not be able to delete the documents later.</p>
	</Alert>
)
const WithRecipientsConfirm = (
	<Alert title="Confirm Document Upload">
		<p>
			Please confirm that the documents you have selected to upload are correct, and you have selected the correct
			recipients.
		</p>
		<p>
			You will not be able to delete the documents later, and an email will immediately be sent to all selected
			recipients.
		</p>
	</Alert>
)

const UploadDocumentsWithRecipientsModal: ReactiveComponent<Props> = (props) => {
	const popups = usePopups()

	type Page = "files" | "recipients"
	const page = ref<Page>("files")
	const files = shallowRef<File[]>([])
	const recipients = shallowRef<string[]>([])

	async function validateSubmit() {
		if (files.value.length === 0) {
			return "Please select at least one file"
		}
		return await popups.confirm({
			content: () => (recipients.value.length === 0 ? NoRecipientsConfirm : WithRecipientsConfirm),
			width: "32rem",
			confirmText: "Upload",
		})
	}

	async function handleSubmit() {
		const hasRecipients = recipients.value.length > 0

		const recipientsText = recipients.value.length === 1 ? "recipient" : "recipients"
		const filesText = files.value.length === 1 ? "file" : "files"
		const confirmText = hasRecipients
			? `Successfully uploaded ${filesText} & sent an email to the selected ${recipientsText}.`
			: `Successfully uploaded ${filesText}.`

		await props.upload(files.value, recipients.value)
		void popups.alertMessage("Success", confirmText)
	}

	const cancelButton = basicFormButton({
		color: "secondary",
		id: "cancel",
		text: "Cancel",
		validateForm: false,
		onSubmit: props.cancel,
	})
	const backButton: FormButton = {
		id: "back",
		validateForm: true,
		onSubmit: () => void (page.value = "files"),
		render: (btnProps) => (
			<BootstrapButton color="secondary" {...btnProps}>
				<Icon icon={faArrowLeft} />
				Back
			</BootstrapButton>
		),
	}

	const nextButton: FormButton = {
		id: "next",
		validateForm: true,
		onSubmit: () => void (page.value = "recipients"),
		render: (btnProps) => (
			<BootstrapButton color="primary" {...btnProps} disabled={files.value.length === 0 || btnProps.disabled}>
				Next
				<Icon icon={faArrowRight} />
			</BootstrapButton>
		),
	}
	const uploadButton: FormButton = {
		id: "submit",
		render: (btnProps) => (
			<BootstrapButton color="primary" {...btnProps}>
				{recipients.value.length === 0 ? "Just Upload" : "Upload & Email"}
			</BootstrapButton>
		),
		validateForm: true,
		validate: validateSubmit,
		onSubmit: handleSubmit,
	}

	const titles: Record<Page, string> = {
		files: "Select Documents to Upload",
		recipients: "Add Email Recipients",
	}
	const buttons: Record<Page, FormButton[]> = {
		files: [cancelButton, nextButton],
		recipients: [backButton, uploadButton],
	}

	return () => (
		<Modal zIndex={3} style={{ width: "56rem", height: "32rem" }}>
			<ModalTitle title={titles[page.value]} />
			<hr />
			<BasicForm buttons={buttons[page.value]} class="flex flex-col h-full">
				{page.value === "files" ? (
					<DocumentsFileUpload class="flex-1" files={files.value} setFiles={refSetter(files)} required />
				) : (
					<SelectRecipients
						options={props.existingRecipients}
						recipients={recipients.value}
						setRecipients={refSetter(recipients)}
					/>
				)}
			</BasicForm>
		</Modal>
	)
}

export default defineComponent(UploadDocumentsWithRecipientsModal, inferProps<Props>())
